import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '../api/client';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

const COLUMN_STORAGE_KEY = 'green-lots.visible-columns';

type GreenLot = {
  id: number;
  lot_code: string;
  product_id: number;
  product_name: string;
  product_sku: string;
  supplier_id: number | null;
  supplier_name?: string | null;
  received_date: string;
  location_id: number | null;
  location_code?: string | null;
  bag_weight: number;
  remaining_weight: number;
  consumed_weight: number;
  status: string;
  price_per_kg: number | null;
  total_value: number | null;
  country_of_origin: string | null;
  crop_year: string | null;
  variety: string | null;
  process: string | null;
  grade: string | null;
  state: string | null;
  moisture: number | null;
  density: number | null;
  screen_size: string | null;
  notes?: string | null;
};

type LotFormValues = {
  product_id: number;
  supplier_id?: number;
  bag_weight: number;
  received_date?: string;
  lot_code?: string;
  location_id?: number;
  price_per_kg?: number;
  total_value?: number;
  country_of_origin?: string;
  crop_year?: string;
  variety?: string;
  process?: string;
  grade?: string;
  state?: string;
  moisture?: number;
  density?: number;
  screen_size?: string;
  notes?: string;
};

type LotEditValues = {
  bag_weight: number;
  remaining_weight: number;
  received_date: string;
  status: string;
  location_id?: number;
  price_per_kg?: number;
  total_value?: number;
  country_of_origin?: string;
  crop_year?: string;
  variety?: string;
  process?: string;
  grade?: string;
  state?: string;
  moisture?: number;
  density?: number;
  screen_size?: string;
  notes?: string;
};

type ColumnKey =
  | 'lot_code'
  | 'received_date'
  | 'location'
  | 'bag_weight'
  | 'remaining_weight'
  | 'consumed_weight'
  | 'status'
  | 'price_per_kg'
  | 'total_value'
  | 'country_of_origin'
  | 'crop_year'
  | 'variety'
  | 'process'
  | 'grade'
  | 'state'
  | 'moisture'
  | 'density'
  | 'screen_size'
  | 'supplier'
  | 'notes';

type ColumnOption = {
  key: ColumnKey;
  label: string;
  align?: 'left' | 'right';
  render: (lot: GreenLot) => ReactNode;
};

const lotStatuses = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: 'depleted', label: 'Depleted' }
];

const GreenLotsPage = () => {
  const queryClient = useQueryClient();
  const [showDepleted, setShowDepleted] = useState(false);
  const [activeTab, setActiveTab] = useState<'inventory' | 'create' | 'columns'>('inventory');
  const [editingLot, setEditingLot] = useState<GreenLot | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingLot(null);
  };

  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<Array<{ id: number; name: string; is_green_coffee: number }>>('/api/products')
  });

  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/api/suppliers')
  });

  const lotsQuery = useQuery({
    queryKey: ['green-lots', { includeDepleted: showDepleted }],
    queryFn: () =>
      api.get<GreenLot[]>(`/api/green-lots${showDepleted ? '?includeDepleted=1' : ''}`)
  });
  const [tableFilter, setTableFilter] = useState('');
  const [sortKey, setSortKey] = useState<ColumnKey>('received_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const createForm = useForm<LotFormValues>({
    defaultValues: {
      bag_weight: 69,
      received_date: new Date().toISOString().slice(0, 10)
    }
  });
  const editForm = useForm<LotEditValues>({
    defaultValues: {
      bag_weight: 0,
      remaining_weight: 0,
      received_date: new Date().toISOString().slice(0, 10),
      status: 'available',
      location_id: undefined,
      price_per_kg: undefined,
      total_value: undefined,
      country_of_origin: '',
      crop_year: '',
      variety: '',
      process: '',
      grade: '',
      state: '',
      moisture: undefined,
      density: undefined,
      screen_size: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (editingLot) {
      editForm.reset({
        bag_weight: editingLot.bag_weight,
        remaining_weight: editingLot.remaining_weight,
        received_date: editingLot.received_date.slice(0, 10),
        status: editingLot.status,
        location_id: editingLot.location_id ?? undefined,
        price_per_kg: editingLot.price_per_kg ?? undefined,
        total_value: editingLot.total_value ?? undefined,
        country_of_origin: editingLot.country_of_origin ?? '',
        crop_year: editingLot.crop_year ?? '',
        variety: editingLot.variety ?? '',
        process: editingLot.process ?? '',
        grade: editingLot.grade ?? '',
        state: editingLot.state ?? '',
        moisture: editingLot.moisture ?? undefined,
        density: editingLot.density ?? undefined,
        screen_size: editingLot.screen_size ?? '',
        notes: editingLot.notes ?? ''
      });
      setIsEditModalOpen(true);
    } else {
      setIsEditModalOpen(false);
    }
  }, [editingLot, editForm]);

  const createLot = useMutation({
    mutationFn: (values: LotFormValues) => {
      const toOptionalNumber = (input?: number) =>
        input !== undefined && !Number.isNaN(input) ? Number(input) : undefined;
      const toOptionalString = (input?: string) =>
        input && input.trim().length > 0 ? input.trim() : undefined;

      const payload = {
        product_id: Number(values.product_id),
        supplier_id:
          values.supplier_id !== undefined && !Number.isNaN(values.supplier_id)
            ? Number(values.supplier_id)
            : undefined,
        bag_weight: Number(values.bag_weight),
        received_date: values.received_date,
        lot_code: toOptionalString(values.lot_code),
        location_id: toOptionalNumber(values.location_id),
        price_per_kg: toOptionalNumber(values.price_per_kg),
        total_value: toOptionalNumber(values.total_value),
        country_of_origin: toOptionalString(values.country_of_origin),
        crop_year: toOptionalString(values.crop_year),
        variety: toOptionalString(values.variety),
        process: toOptionalString(values.process),
        grade: toOptionalString(values.grade),
        state: toOptionalString(values.state),
        moisture: toOptionalNumber(values.moisture),
        density: toOptionalNumber(values.density),
        screen_size: toOptionalString(values.screen_size),
        notes: toOptionalString(values.notes)
      };
      return api.post<GreenLot>('/api/green-lots', payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'green-lots' });
      queryClient.invalidateQueries({ queryKey: ['inventory-summary'] });
      createForm.reset({
        bag_weight: 69,
        received_date: new Date().toISOString().slice(0, 10),
        lot_code: undefined,
        supplier_id: undefined,
        location_id: undefined,
        price_per_kg: undefined,
        total_value: undefined,
        country_of_origin: undefined,
        crop_year: undefined,
        variety: undefined,
        process: undefined,
        grade: undefined,
        state: undefined,
        moisture: undefined,
        density: undefined,
        screen_size: undefined,
        notes: undefined
      });
      setFeedback({ type: 'success', message: 'Green lot created successfully.' });
    },
    onError: (error) => {
      console.error(error);
      setFeedback({ type: 'error', message: 'Unable to create green lot. Please try again.' });
    }
  });

  const updateLot = useMutation({
    mutationFn: ({ id, values }: { id: number; values: LotEditValues }) => {
      const toOptionalNumber = (input?: number | null) =>
        input !== undefined && input !== null && !Number.isNaN(input) ? Number(input) : undefined;
      const toOptionalString = (input?: string | null) =>
        input && input.trim().length > 0 ? input.trim() : undefined;

      return api.put<GreenLot>(`/api/green-lots/${id}`, {
        bag_weight: Number(values.bag_weight),
        remaining_weight: Number(values.remaining_weight),
        received_date: values.received_date,
        status: values.status,
        location_id: toOptionalNumber(values.location_id),
        price_per_kg: toOptionalNumber(values.price_per_kg),
        total_value: toOptionalNumber(values.total_value),
        country_of_origin: toOptionalString(values.country_of_origin),
        crop_year: toOptionalString(values.crop_year),
        variety: toOptionalString(values.variety),
        process: toOptionalString(values.process),
        grade: toOptionalString(values.grade),
        state: toOptionalString(values.state),
        moisture: toOptionalNumber(values.moisture),
        density: toOptionalNumber(values.density),
        screen_size: toOptionalString(values.screen_size),
        notes: toOptionalString(values.notes)
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'green-lots' });
      closeEditModal();
      setFeedback({ type: 'success', message: 'Green lot updated successfully.' });
    },
    onError: (error) => {
      console.error(error);
      setFeedback({ type: 'error', message: 'Unable to update green lot. Please try again.' });
    }
  });

  const lots = useMemo(() => lotsQuery.data ?? [], [lotsQuery.data]);
  const greenProducts = useMemo(
    () => (productsQuery.data ?? []).filter((product) => product.is_green_coffee === 1),
    [productsQuery.data]
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );

  const columnOptions = useMemo<ColumnOption[]>(() => {
    const formatCurrency = (value: number | null | undefined) =>
      value != null ? currencyFormatter.format(value) : '—';
    const formatKg = (value: number | null | undefined) =>
      value != null ? `${value.toFixed(2)} kg` : '—';
    const formatDate = (value?: string | null) => {
      if (!value) return '—';
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
    };
    const formatPercent = (value: number | null | undefined) =>
      value != null ? `${value.toFixed(2)}%` : '—';
    const formatDensity = (value: number | null | undefined) =>
      value != null ? `${value.toFixed(1)} g/L` : '—';
    const fallback = (value?: string | null) => (value && value.trim().length > 0 ? value : '—');
    const shortNotes = (value?: string | null) => {
      if (!value || value.trim().length === 0) return '—';
      return value.length > 80 ? `${value.slice(0, 77)}…` : value;
    };

    return [
      {
        key: 'lot_code',
        label: 'Green Lot Code',
        render: (lot: GreenLot) => (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <strong>{lot.lot_code}</strong>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              {lot.product_name} ({lot.product_sku})
            </span>
          </div>
        )
      },
      {
        key: 'received_date',
        label: 'Received Date',
        render: (lot) => formatDate(lot.received_date)
      },
      {
        key: 'location',
        label: 'Location',
        render: (lot) => lot.location_code ?? (lot.location_id ? `#${lot.location_id}` : '—')
      },
      {
        key: 'bag_weight',
        label: 'Bag (kg)',
        align: 'right',
        render: (lot) => formatKg(lot.bag_weight)
      },
      {
        key: 'remaining_weight',
        label: 'Remaining (kg)',
        align: 'right',
        render: (lot) => formatKg(lot.remaining_weight)
      },
      {
        key: 'consumed_weight',
        label: 'Consumed (kg)',
        align: 'right',
        render: (lot) => formatKg(lot.consumed_weight)
      },
      {
        key: 'status',
        label: 'Status',
        render: (lot) => <span className="badge">{lot.status}</span>
      },
      {
        key: 'price_per_kg',
        label: 'Price per kg',
        align: 'right',
        render: (lot) => formatCurrency(lot.price_per_kg)
      },
      {
        key: 'total_value',
        label: 'Total value',
        align: 'right',
        render: (lot) => {
          const fallbackValue =
            lot.total_value ?? (lot.price_per_kg != null ? lot.price_per_kg * lot.bag_weight : null);
          return formatCurrency(fallbackValue);
        }
      },
      {
        key: 'country_of_origin',
        label: 'Country of Origin',
        render: (lot) => fallback(lot.country_of_origin)
      },
      {
        key: 'crop_year',
        label: 'Crop Year',
        render: (lot) => fallback(lot.crop_year)
      },
      {
        key: 'variety',
        label: 'Variety',
        render: (lot) => fallback(lot.variety)
      },
      {
        key: 'process',
        label: 'Process',
        render: (lot) => fallback(lot.process)
      },
      {
        key: 'grade',
        label: 'Grade',
        render: (lot) => fallback(lot.grade)
      },
      {
        key: 'state',
        label: 'State',
        render: (lot) => fallback(lot.state)
      },
      {
        key: 'moisture',
        label: 'Moisture',
        align: 'right',
        render: (lot) => formatPercent(lot.moisture)
      },
      {
        key: 'density',
        label: 'Density',
        align: 'right',
        render: (lot) => formatDensity(lot.density)
      },
      {
        key: 'screen_size',
        label: 'Screen Size',
        render: (lot) => fallback(lot.screen_size)
      },
      {
        key: 'supplier',
        label: 'Supplier',
        render: (lot) => fallback(lot.supplier_name)
      },
      {
        key: 'notes',
        label: 'Notes',
        render: (lot) => shortNotes(lot.notes)
      }
    ];
  }, [currencyFormatter]);

  const [selectedColumnKeys, setSelectedColumnKeys] = useState<ColumnKey[]>(() =>
    columnOptions.map((column) => column.key)
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(COLUMN_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as ColumnKey[];
      const valid = parsed.filter((key) => columnOptions.some((column) => column.key === key));
      if (valid.length > 0) {
        setSelectedColumnKeys(valid);
      }
    } catch {
      // ignore invalid stored values
    }
  }, [columnOptions]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(selectedColumnKeys));
  }, [selectedColumnKeys]);

  const handleColumnToggle = (key: ColumnKey) => {
    setSelectedColumnKeys((previous) => {
      if (previous.includes(key)) {
        if (previous.length === 1) {
          return previous;
        }
        return previous.filter((item) => item !== key);
      }
      return [...previous, key];
    });
  };

  const visibleColumns = useMemo(
    () => columnOptions.filter((column) => selectedColumnKeys.includes(column.key)),
    [columnOptions, selectedColumnKeys]
  );

  const rows = useMemo(() => {
    const f = tableFilter.trim().toLowerCase();
    const filtered = (lots ?? []).filter((lot) => {
      if (!f) return true;
      const hay = [
        lot.lot_code,
        lot.product_name,
        lot.product_sku,
        lot.supplier_name ?? '',
        lot.location_code ?? (lot.location_id ? `#${lot.location_id}` : ''),
        lot.status,
        lot.country_of_origin ?? '',
        lot.crop_year ?? '',
        lot.variety ?? '',
        lot.process ?? '',
        lot.grade ?? '',
        lot.state ?? '',
        lot.screen_size ?? '',
        String(lot.bag_weight),
        String(lot.remaining_weight),
        String(lot.consumed_weight),
        String(lot.moisture ?? ''),
        String(lot.density ?? ''),
        lot.notes ?? ''
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    const getSortValue = (lot: GreenLot): string | number => {
      switch (sortKey) {
        case 'lot_code':
          return lot.lot_code;
        case 'received_date':
          return new Date(lot.received_date).getTime();
        case 'location':
          return lot.location_code ?? (lot.location_id ? `#${lot.location_id}` : '');
        case 'bag_weight':
          return lot.bag_weight;
        case 'remaining_weight':
          return lot.remaining_weight;
        case 'consumed_weight':
          return lot.consumed_weight;
        case 'status':
          return lot.status;
        case 'price_per_kg':
          return lot.price_per_kg ?? Number.NEGATIVE_INFINITY;
        case 'total_value':
          return lot.total_value ?? (lot.price_per_kg != null ? lot.price_per_kg * lot.bag_weight : Number.NEGATIVE_INFINITY);
        case 'country_of_origin':
          return lot.country_of_origin ?? '';
        case 'crop_year':
          return lot.crop_year ?? '';
        case 'variety':
          return lot.variety ?? '';
        case 'process':
          return lot.process ?? '';
        case 'grade':
          return lot.grade ?? '';
        case 'state':
          return lot.state ?? '';
        case 'moisture':
          return lot.moisture ?? Number.NEGATIVE_INFINITY;
        case 'density':
          return lot.density ?? Number.NEGATIVE_INFINITY;
        case 'screen_size':
          return lot.screen_size ?? '';
        case 'supplier':
          return lot.supplier_name ?? '';
        case 'notes':
          return lot.notes ?? '';
      }
    };

    return filtered.sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [lots, tableFilter, sortKey, sortDir]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'space-between' }}>
        <div>
          <h2>Green Coffee Lots</h2>
          <p style={{ color: '#64748b' }}>
            Track individual sacks with unique lot codes so every roast remains traceable back to its
            source.
          </p>
        </div>
      </div>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'inventory', label: 'Lot Inventory', badge: rows.length },
          { id: 'create', label: 'Add Green Lot' },
          { id: 'columns', label: 'Column Settings' }
        ]}
      />

      {feedback && (
        <div
          className="card"
          style={{
            marginTop: 16,
            borderLeft: feedback.type === 'error' ? '4px solid #ef4444' : '4px solid #22c55e',
            color: feedback.type === 'error' ? '#b91c1c' : '#166534',
            backgroundColor: feedback.type === 'error' ? '#fee2e2' : '#dcfce7'
          }}
        >
          {feedback.message}
        </div>
      )}

      {activeTab === 'columns' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 12 }}>Visible columns</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {columnOptions.map((option) => (
              <label
                key={option.key}
                style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#475569' }}
              >
                <input
                  type="checkbox"
                  checked={selectedColumnKeys.includes(option.key)}
                  onChange={() => handleColumnToggle(option.key)}
                />
                {option.label}
              </label>
            ))}
          </div>
          <p style={{ color: '#94a3b8', marginTop: 12, fontSize: 12 }}>
            At least one column must remain visible.
          </p>
        </div>
      ) : null}

      {activeTab === 'create' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Add a lot</h3>
          <form
            className="form-grid"
            onSubmit={createForm.handleSubmit(async (values) => {
              setFeedback(null);
              try {
                await createLot.mutateAsync(values);
              } catch (error) {
                console.error(error);
              }
            })}
          >
          <div className="form-group">
            <label>Green coffee</label>
            <select {...createForm.register('product_id', { valueAsNumber: true, required: true })}>
              <option value="">Select product</option>
              {greenProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Supplier</label>
            <select {...createForm.register('supplier_id', { valueAsNumber: true })}>
              <option value="">Select supplier</option>
              {suppliersQuery.data?.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Location ID</label>
            <input
              type="number"
              step="1"
              {...createForm.register('location_id', { valueAsNumber: true })}
            />
          </div>
          <div className="form-group">
            <label>Bag weight (kg)</label>
            <input type="number" step="0.01" {...createForm.register('bag_weight', { valueAsNumber: true, required: true })} />
          </div>
          <div className="form-group">
            <label>Received date</label>
            <input type="date" {...createForm.register('received_date')} />
          </div>
          <div className="form-group">
            <label>Lot code (optional)</label>
            <input placeholder="Auto-generated if blank" {...createForm.register('lot_code')} />
          </div>
          <div className="form-group">
            <label>Price per kg</label>
            <input type="number" step="0.01" {...createForm.register('price_per_kg', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Total value</label>
            <input type="number" step="0.01" {...createForm.register('total_value', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Country of origin</label>
            <input {...createForm.register('country_of_origin')} />
          </div>
          <div className="form-group">
            <label>Crop year</label>
            <input {...createForm.register('crop_year')} />
          </div>
          <div className="form-group">
            <label>Variety</label>
            <input {...createForm.register('variety')} />
          </div>
          <div className="form-group">
            <label>Process</label>
            <input {...createForm.register('process')} />
          </div>
          <div className="form-group">
            <label>Grade</label>
            <input {...createForm.register('grade')} />
          </div>
          <div className="form-group">
            <label>State / Region</label>
            <input {...createForm.register('state')} />
          </div>
          <div className="form-group">
            <label>Moisture %</label>
            <input type="number" step="0.1" {...createForm.register('moisture', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Density (g/L)</label>
            <input type="number" step="0.1" {...createForm.register('density', { valueAsNumber: true })} />
          </div>
          <div className="form-group">
            <label>Screen size</label>
            <input {...createForm.register('screen_size')} />
          </div>
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>Notes</label>
            <textarea rows={2} {...createForm.register('notes')} />
          </div>
          <button className="btn primary" type="submit" disabled={createLot.isPending}>
            {createLot.isPending ? 'Saving...' : 'Create lot'}
          </button>
          </form>
        </div>
      ) : null}

      {activeTab === 'inventory' ? (
        <section style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <h3 style={{ margin: 0 }}>Lot inventory</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
              <input
                type="checkbox"
                checked={showDepleted}
                onChange={(event) => setShowDepleted(event.target.checked)}
              />
              Show depleted
            </label>
          </div>
          <TableToolbar value={tableFilter} onChange={setTableFilter} placeholder="Filter lots…" />
          {lots.length === 0 && !lotsQuery.isLoading ? (
            <div className="empty-state">No green coffee lots recorded yet.</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() => {
                          if (sortKey === column.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          setSortKey(column.key as ColumnKey);
                        }}
                        style={{ cursor: 'pointer', textAlign: column.align === 'right' ? 'right' : 'left' }}
                      >
                        {column.label}
                        {sortKey === column.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((lot) => (
                    <tr key={lot.id}>
                      {visibleColumns.map((column) => (
                        <td key={column.key} style={column.align === 'right' ? { textAlign: 'right' } : undefined}>
                          {column.render(lot)}
                        </td>
                      ))}
                      <td>
                        <button
                          className="btn secondary"
                          type="button"
                          onClick={() => {
                            setFeedback(null);
                            setEditingLot(lot);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}

      {isEditModalOpen && editingLot && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            zIndex: 1000
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="card"
            style={{ width: 'min(950px, 95vw)', maxHeight: '90vh', overflowY: 'auto', position: 'relative' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}>Edit lot</h3>
                <p style={{ color: '#64748b', margin: '4px 0 0' }}>{editingLot.lot_code}</p>
              </div>
              <button className="btn" type="button" onClick={closeEditModal}>
                Close
              </button>
            </div>
            <form
              className="form-grid"
              onSubmit={editForm.handleSubmit(async (values) => {
                if (!editingLot) return;
                setFeedback(null);
                try {
                  await updateLot.mutateAsync({ id: editingLot.id, values });
                } catch (error) {
                  console.error(error);
                }
              })}
            >
              <div className="form-group">
                <label>Bag weight (kg)</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  {...editForm.register('bag_weight', { valueAsNumber: true })}
                />
              </div>
              <div className="form-group">
                <label>Remaining weight (kg)</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  {...editForm.register('remaining_weight', { valueAsNumber: true })}
                />
              </div>
              <div className="form-group">
                <label>Received date</label>
                <input type="date" {...editForm.register('received_date')} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select {...editForm.register('status')}>
                  {lotStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Location ID</label>
                <input type="number" step="1" {...editForm.register('location_id', { valueAsNumber: true })} />
              </div>
              <div className="form-group">
                <label>Price per kg</label>
                <input type="number" step="0.01" {...editForm.register('price_per_kg', { valueAsNumber: true })} />
              </div>
              <div className="form-group">
                <label>Total value</label>
                <input type="number" step="0.01" {...editForm.register('total_value', { valueAsNumber: true })} />
              </div>
              <div className="form-group">
                <label>Country of origin</label>
                <input {...editForm.register('country_of_origin')} />
              </div>
              <div className="form-group">
                <label>Crop year</label>
                <input {...editForm.register('crop_year')} />
              </div>
              <div className="form-group">
                <label>Variety</label>
                <input {...editForm.register('variety')} />
              </div>
              <div className="form-group">
                <label>Process</label>
                <input {...editForm.register('process')} />
              </div>
              <div className="form-group">
                <label>Grade</label>
                <input {...editForm.register('grade')} />
              </div>
              <div className="form-group">
                <label>State / Region</label>
                <input {...editForm.register('state')} />
              </div>
              <div className="form-group">
                <label>Moisture %</label>
                <input type="number" step="0.1" {...editForm.register('moisture', { valueAsNumber: true })} />
              </div>
              <div className="form-group">
                <label>Density (g/L)</label>
                <input type="number" step="0.1" {...editForm.register('density', { valueAsNumber: true })} />
              </div>
              <div className="form-group">
                <label>Screen size</label>
                <input {...editForm.register('screen_size')} />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Notes</label>
                <textarea rows={3} {...editForm.register('notes')} />
              </div>
              <div style={{ display: 'flex', gap: 12, gridColumn: 'span 2' }}>
                <button className="btn primary" type="submit" disabled={updateLot.isPending}>
                  {updateLot.isPending ? 'Saving...' : 'Save changes'}
                </button>
                <button className="btn" type="button" onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GreenLotsPage;
